import { useLazyQuery, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Flex, Text, Card } from 'rebass/styled-components'
import { Skeleton } from '../../components/loadingSkeleton/Skeleton';
import { GET_CURRENT_INTAKE_VALUES } from '../../graphql/queries';
import { IAllergyHistory, IFamilyHistory, IGetPatientIntakeValuesInput, IGetPatientIntakeValuesOutput, IGetProviderCurrentIntakeValues, IMedicalHistory, IPatientProblemsHistory, ISurgicalHistory } from '../../graphql/types/queries/charts';
import { Medications } from './components/Medications';
import { useIsVisible } from '../../hooks/useIsVisible';

interface IChartsProps {
  id: string
}

const RND_SIZES = [150, 137, 187, 211, 111, 237]

const LoadingPlaceholder = () => {
  return <Flex flexDirection={'column'} width='100%' padding={'20px'}>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #FFABA0' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Medical history</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #B8D6F5' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>GYN history</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #BCFFEB' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Family history</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #344F7A' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Social history</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #be6703' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Surgical history</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
    <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #269744' }} paddingY={'12px'} mb={4}>
      <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>{`Problems / Symptoms`}</Text>
      {[0, 1, 2, 3].map(x => {
        return <Skeleton width={RND_SIZES[Math.floor(Math.random() * RND_SIZES.length)]} />
      })}
    </Flex>
  </Flex>
}

export const Charts: React.FC<IChartsProps> = ({ id }) => {

  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);
  const [load, { data, loading, error }] = useLazyQuery<IGetPatientIntakeValuesOutput, IGetPatientIntakeValuesInput>(GET_CURRENT_INTAKE_VALUES, {
    variables: {
      id
    }
  })
  const [showActiveProblems, setShowActiveProblems] = useState<boolean>(true)

  useEffect(() => {
    if (!!isVisible) {
      load()
    }
  }, [isVisible])
  
  return (
    <Flex ref={ref} flexDirection={'column'} width='100%' padding={'20px'}>
      {loading ? (<LoadingPlaceholder />) : (
        <>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #FFABA0' }} paddingY={'12px'} mb={4}>
            <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Medical history</Text>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).medicalhistory || [] as IMedicalHistory[]).map(x => {
              return (
                <Flex p={2} ml='12px' style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  <Text fontWeight={500}><span style={{ color: '#838485', fontWeight: 300 }}>{x.question}:</span> {x.note && x.note.length > 2 ? x.note : x.answer === 'N' ? 'No' : x.answer === 'Y' ? 'Yes' : x.answer}</Text>
                </Flex>
              )
            })}
            {(!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.medicalhistory || data.getProviderCurrentIntakeValues.medicalhistory.length < 1) && (
              <Text color='#a2aab6' textAlign={'center'}>No medical history data</Text>
            )}
          </Flex>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #B8D6F5' }} paddingY={'12px'} mb={4}>
            <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>GYN history</Text>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).gynhistory || [] as IMedicalHistory[]).map(x => {
              return (
                <Flex p={2} ml='12px' style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  <Text fontWeight={500}><span style={{ color: '#838485', fontWeight: 300 }}>{x.question}:</span> {x.note && x.note.length > 2 ? x.note : x.answer === 'N' ? 'No' : x.answer === 'Y' ? 'Yes' : x.answer}</Text>
                </Flex>
              )
            })}
            {(!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.gynhistory || data.getProviderCurrentIntakeValues.gynhistory.length < 1) && (
              <Text color='#a2aab6' textAlign={'center'}>No GYN data</Text>
            )}
          </Flex>

          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #BCFFEB' }} paddingY={'12px'} mb={4}>
            <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Family history</Text>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).familyHistory || [] as IFamilyHistory[]).map(x => {
              return (
                <Flex p={2} ml='12px' key={`${x.relation}-${x.problems?.map(z => z?.problemId).join(',')}`} flexDirection={'column'} style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  {x.problems?.map(y => (
                    <Text>{y?.note || y?.description}</Text>
                  ))}
                  <Text fontSize={'14px'} ml={1} fontWeight={300} color='#59575d'>{x.relation}</Text>
                </Flex>
              )
            })}
            {(!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.familyHistory || data.getProviderCurrentIntakeValues.familyHistory.length < 1) && (
              <Text color='#a2aab6' textAlign={'center'}>No family history data</Text>
            )}
          </Flex>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #344F7A' }} paddingY={'12px'} mb={4}>
            <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Social history</Text>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).socialHistory || [] as IMedicalHistory[]).map(x => {
              return (
                <Flex p={2} ml='12px' style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  <Text fontWeight={500}><span style={{ color: '#838485', fontWeight: 300 }}>{x.question}: </span>{x.note && x.note.length > 2 ? x.note : x.answer === 'N' ? 'No' : x.answer === 'Y' ? 'Yes' : x.answer}</Text>
                </Flex>
              )
            })}
            {(!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.socialHistory || data.getProviderCurrentIntakeValues.socialHistory.length < 1) && (
              <Text color='#a2aab6' textAlign={'center'}>No social history data</Text>
            )}
          </Flex>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #be6703' }} paddingY={'12px'} mb={4}>
            <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Surgical history</Text>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).surgicalHistory || [] as ISurgicalHistory[]).map(x => {
              return (
                <Flex p={2} ml='12px' style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  <Text>{x.description}</Text>
                  <Text ml={2}>{x.note}</Text>
                </Flex>
              )
            })}
            {!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.surgicalHistory || (data.getProviderCurrentIntakeValues.surgicalHistory || []).length < 1 ? (
              <Text color='#a2aab6' textAlign={'center'}>No surgical history data</Text>
            ) : (
              null
            )}
          </Flex>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', paddingLeft: '20px', borderLeft: '3px solid #269744' }} paddingY={'12px'} mb={4}>

            <Flex flexDirection={['column', 'row', 'row']} m={'18px 12px'} alignItems='baseline'>
              <Text fontWeight={600} fontSize='18px' m={'18px 12px'}>Problems / Symptoms</Text>
              {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).problems || [] as IPatientProblemsHistory[]).filter(x => x.events && x.events.eventtype === 'HIDDEN').length > 0 && (
                <Text fontWeight={300} fontSize='14px' color={'#7092c7'} style={{ cursor: 'pointer' }} onClick={() => setShowActiveProblems(!showActiveProblems)}>{`${showActiveProblems ? `HISTORICAL (${((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).problems || [] as IPatientProblemsHistory[]).filter(x => x.events && x.events.eventtype === 'HIDDEN').length})` : `ACTIVE (${((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).problems || [] as IPatientProblemsHistory[]).filter(x => x.events && x.events.eventtype !== 'HIDDEN').length})`}`}</Text>
              )}
            </Flex>

            {((data?.getProviderCurrentIntakeValues || {} as IGetProviderCurrentIntakeValues).problems || [] as IPatientProblemsHistory[]).filter(x => x.events && (showActiveProblems ? x.events.eventtype !== 'HIDDEN' : x.events.eventtype === 'HIDDEN')).map(x => {
              return (
                <Flex p={2} ml='12px' style={{ borderLeft: '3px solid #f8a2945c' }} mt={2}>
                  <Text style={{ textDecoration: showActiveProblems ? '' : 'line-through' }}>{x.name}</Text>
                  <Text style={{ textDecoration: showActiveProblems ? '' : 'line-through' }} ml={2}>{x.events ? x.events.note : ''}</Text>
                </Flex>
              )
            })}
            {!data || !data.getProviderCurrentIntakeValues || !data.getProviderCurrentIntakeValues.problems || (data.getProviderCurrentIntakeValues.problems || []).length < 1 ? (
              <Text color='#a2aab6' textAlign={'center'}>No problems history data</Text>
            ) : (
              null
            )}
          </Flex>
        </>
      )}
    </Flex>);
}